import React, {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {Col, message, Row, Typography} from 'antd';
import ENDPOINTS from '../../constants/endpoints';
import {ROUTES, formatPath} from '../../constants/PathRoutes';
import api from '../../services/api';
import useDefaultRequest from '../../hooks/useDefaultRequest';
import Form from '../../components/data_entry/form/Form';
import InputFormField from '../../components/data_entry/form/InputFormField';
import TextAreaFormField from '../../components/data_entry/form/TextAreaFormField';
import SwitchFormField from '../../components/data_entry/form/SwitchFormField';
import InputNumberFormField from '../../components/data_entry/form/InputNumberFormField';
import ImageCropFormField from '../../components/data_entry/form/ImageCropFormField';
import SelectFormField from '../../components/data_entry/form/SelectFormField';
import Button from '../../components/data_entry/Button';
import Loading from '../../components/admin/Loading';
import {BuildFieldErrorsDict} from '../../utils/utils';
import {MASK_TYPES_INPUT} from "../../constants/constants";

const FORM_NAMES = {
    NAME: 'name',
    IMAGE: 'image',
    DESCRIPTION: 'description',
    PRICE: 'price',
    CATEGORY: 'category',
    IS_ACTIVE: 'is_active',
    REQUIRES_PRESCRIPTION: 'requires_prescription',
};

export default function AdminProductForm() {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const {id} = useParams();
    const isEditing = !!id;

    const {data, isLoading: fetching} = useDefaultRequest({
        url: formatPath(ENDPOINTS.ECOMMERCE_PRODUCT_DETAIL, {id}),
        makeRequest: isEditing,
        redirectRoute: ROUTES.ADMIN_ECOMMERCE_PRODUCT_LIST
    });

    useEffect(() => {
        if (data && isEditing) {
            form.setFieldsValue({
                ...data,
                [FORM_NAMES.IMAGE]: data[FORM_NAMES.IMAGE] || null
            });
        }
    }, [data, isEditing, form]);

    const onFinish = async (values) => {
        setLoading(true);
        try {
            const formData = new FormData();

            formData.append(FORM_NAMES.NAME, values[FORM_NAMES.NAME]);
            formData.append(FORM_NAMES.DESCRIPTION, values[FORM_NAMES.DESCRIPTION] || '');
            formData.append(FORM_NAMES.PRICE, values[FORM_NAMES.PRICE]);
            formData.append(FORM_NAMES.CATEGORY, values[FORM_NAMES.CATEGORY]);
            formData.append(FORM_NAMES.IS_ACTIVE, values[FORM_NAMES.IS_ACTIVE] ?? true);
            formData.append(FORM_NAMES.REQUIRES_PRESCRIPTION, values[FORM_NAMES.REQUIRES_PRESCRIPTION] ?? false);

            if (values[FORM_NAMES.IMAGE] && values[FORM_NAMES.IMAGE].file) {
                formData.append(FORM_NAMES.IMAGE, values[FORM_NAMES.IMAGE].file);
            }

            if (isEditing) {
                await api.patch(formatPath(ENDPOINTS.ECOMMERCE_PRODUCT_DETAIL, {id}), formData, {
                    headers: {'Content-Type': 'multipart/form-data'}
                });
                message.success('Produto atualizado com sucesso');
            } else {
                await api.post(ENDPOINTS.ECOMMERCE_PRODUCT_LIST, formData, {
                    headers: {'Content-Type': 'multipart/form-data'}
                });
                message.success('Produto criado com sucesso');
            }
            navigate(ROUTES.ADMIN_ECOMMERCE_PRODUCT_LIST);
        } catch (error) {
            BuildFieldErrorsDict(error, form.setFields);
        } finally {
            setLoading(false);
        }
    };

    if (fetching) {
        return <Loading />;
    }

    return (
        <Row>
            <Col xs={24}>
                <Typography.Title style={{marginTop: 0}} level={2}>
                    {isEditing ? 'Editar Produto' : 'Novo Produto'}
                </Typography.Title>
            </Col>
            <Col xs={24}>
                <Form
                    form={form}
                    onFinish={onFinish}
                    initialValues={{
                        [FORM_NAMES.IS_ACTIVE]: true,
                        [FORM_NAMES.REQUIRES_PRESCRIPTION]: false,
                    }}
                >
                    <Row gutter={[16, 0]}>
                        <Col xs={24}>
                            <Row>
                                <Col xs={12} sm={4}>
                                    <SwitchFormField
                                        form={{
                                            label: 'Ativo',
                                            name: FORM_NAMES.IS_ACTIVE,
                                        }}
                                    />
                                </Col>
                                <Col xs={12} sm={4}>
                                    <SwitchFormField
                                        form={{
                                            label: 'Receita Obrigatória',
                                            name: FORM_NAMES.REQUIRES_PRESCRIPTION,
                                            tooltip: 'Marque se o produto exige receita médica'
                                        }}
                                    />
                                </Col>
                            </Row>
                        </Col>
                        <Col xs={8}>
                            <ImageCropFormField
                                form={{
                                    label: 'Imagem',
                                    name: FORM_NAMES.IMAGE,
                                    rules: [{
                                        required: !isEditing,
                                        message: 'Selecione uma imagem'
                                    }]
                                }}
                                aspect={1}
                                height={180}
                                placeholder="Adicionar imagem"
                            />
                        </Col>
                        <Col xs={24} lg={16}>
                            <Row gutter={[16, 0]}>
                                <Col xs={24}>
                                    <InputFormField
                                        placeholder="Digite o nome do produto"
                                        form={{
                                            label: 'Nome',
                                            name: FORM_NAMES.NAME,
                                            rules: [{
                                                required: true,
                                                message: 'Informe o nome'
                                            }]
                                        }}
                                    />
                                </Col>
                                <Col xs={24} lg={12}>
                                    <InputFormField
                                        maskType={MASK_TYPES_INPUT.MONEY}
                                        prefix="R$"
                                        form={{
                                            label: 'Preço',
                                            name: FORM_NAMES.PRICE,
                                            rules: [{
                                                required: true,
                                                message: 'Informe o preço'
                                            }]
                                        }}
                                    />

                                </Col>
                                <Col xs={24} lg={12}>
                                    <SelectFormField
                                        placeholder="Selecione a categoria"
                                        request={{
                                            url: ENDPOINTS.ECOMMERCE_CATEGORY_LIST,
                                            labelField: 'name', 
                                        }}
                                        form={{
                                            label: 'Categoria',
                                            name: FORM_NAMES.CATEGORY,
                                            rules: [{
                                                required: true,
                                                message: 'Selecione uma categoria'
                                            }]
                                        }}
                                    />
                                </Col>
                            </Row>
                        </Col>
                        <Col xs={24}>
                            <TextAreaFormField
                                rows={4}
                                placeholder="Descrição do produto"
                                form={{
                                    label: 'Descrição',
                                    name: FORM_NAMES.DESCRIPTION,
                                }}
                            />
                        </Col>

                        <Col xs={24} style={{textAlign: 'right', marginTop: 24}}>
                            <Button
                                type="default"
                                style={{marginRight: 8}}
                                onClick={() => navigate(ROUTES.ADMIN_ECOMMERCE_PRODUCT_LIST)}
                            >
                                Cancelar
                            </Button>
                            <Button
                                onClick={form.submit}
                                loading={loading}
                            >
                                Salvar
                            </Button>
                        </Col>
                    </Row>
                </Form>
            </Col>
        </Row>
    );
}
